/**
 * A copied gateway → its twin in this country (v2, 26/10/2026: the route on
 * the same SMSWiser gateway first in the list), another of this country's
 * gateways, or Leave out (its pool lanes and drop pins stay behind). Names
 * read as the drop rows show them; the full route name sits in the tooltip
 * and the search. Its sender IDs come along unless cleared.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { GroupCopyGatewayRow } from '@trafficpulse/shared';

import { SearchableSelect, type SearchableSelectOption } from '@/components/ui';
import { routeLabel } from '@/features/drop-generator/components/timeline/row/GatewayCell';

import { matchBadge } from '../MatchBadge';
import { PickRow, usedByLine } from '../PickRow';
import { SenderIds } from './SenderIds';

export const LEAVE_OUT = 'out';

interface Props {
  row: GroupCopyGatewayRow;
  options: Array<{ routeId: number; name: string }>;
  answered: boolean;
  onPick: (target: number | null) => void;
  onClearSenders: (clear: boolean) => void;
  /** In a fold: one line (27/10/2026). */
  compact?: boolean;
}

const short = (name: string) => routeLabel(name) ?? name;

/** The same gateway's routes first (best first), then every other one, then Leave out. */
function gatewayOptions(row: GroupCopyGatewayRow, options: Props['options']): SearchableSelectOption<string>[] {
  const first = new Set(row.candidates);
  const ordered = [...row.candidates.flatMap((id) => options.filter((o) => o.routeId === id)), ...options.filter((o) => !first.has(o.routeId))];
  const routes = ordered.map((o) => {
    const tag = first.has(o.routeId) && row.gatewayName ? <span className="ml-1.5 text-[10px] text-muted-foreground">{row.gatewayName}</span> : null;
    const optionContent = <span className="flex min-w-0 items-center truncate" title={o.name}>{short(o.name)}{tag}</span>;
    const triggerContent = <span className="block truncate" title={o.name}>{short(o.name)}</span>;
    return { value: String(o.routeId), label: o.name, optionContent, triggerContent };
  });
  return [...routes, { value: LEAVE_OUT, label: 'Leave out' }];
}

const gatewayMeta = (row: GroupCopyGatewayRow) => `${row.gatewayName ? `${row.gatewayName} gateway · ` : ''}${usedByLine(row.usedBy)}`;

export const GatewayPick: FC<Props> = ({ row, options, answered, onPick, onClearSenders, compact }) => {
  const value = row.target !== null ? String(row.target) : answered || row.how !== 'none' ? LEAVE_OUT : '';
  return (
    <PickRow
      compact={compact}
      name={row.sourceName}
      source={<span className="truncate" title={row.sourceName}>{short(row.sourceName)}</span>}
      select={<SearchableSelect options={gatewayOptions(row, options)} value={value} onChange={(v) => onPick(v === LEAVE_OUT ? null : Number(v))} placeholder="Pick a gateway…" searchPlaceholder="Search a gateway…" className="h-8" />}
      badge={matchBadge(row.how, row.target !== null, answered)}
      meta={gatewayMeta(row)}
      extra={<SenderIds ids={row.senderIds ?? []} cleared={row.sendersCleared ?? false} lands={row.target !== null} onClear={onClearSenders} />}
      onConfirm={row.target !== null ? () => onPick(row.target) : undefined}
    />
  );
};
